import { useLayoutEffect, type RefObject } from "react";

/** Isolate a custom modal, including background controls outside React's root. */
export function useModalDialog(open: boolean, ref: RefObject<HTMLElement | null>, close: () => void) {
  useLayoutEffect(() => {
    const dialog = ref.current;
    if (!open || !dialog) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const changed: Array<[HTMLElement, boolean]> = [];
    let branch = dialog.parentElement;
    while (branch?.parentElement) {
      for (const sibling of branch.parentElement.children) {
        if (sibling !== branch && sibling instanceof HTMLElement) {
          changed.push([sibling, sibling.inert]); sibling.inert = true;
        }
      }
      branch = branch.parentElement;
      if (branch === document.body) break;
    }
    const priorOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const controls = () => Array.from(dialog.querySelectorAll<HTMLElement>(
      "button:not(:disabled), a[href], textarea:not(:disabled), input:not(:disabled), select:not(:disabled), [tabindex='0']",
    )).filter(node => !node.inert && node.getClientRects().length > 0 && getComputedStyle(node).visibility !== "hidden");
    const focusStart = () => (dialog.querySelector<HTMLElement>("[data-modal-close]") ?? controls()[0] ?? dialog)
      .focus({ preventScroll: true });
    focusStart();
    const focusGuard = (event: FocusEvent) => { if (!dialog.contains(event.target as Node)) focusStart(); };
    const keyboard = (event: KeyboardEvent) => {
      if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); close(); }
      else if (event.key === "Tab") {
        const items = controls(), first = items[0], last = items.at(-1);
        if (!first || !last) { event.preventDefault(); focusStart(); }
        else if (!dialog.contains(document.activeElement) || event.shiftKey && document.activeElement === first ||
          !event.shiftKey && document.activeElement === last) {
          event.preventDefault(); (event.shiftKey ? last : first).focus({ preventScroll: true });
        }
      }
    };
    document.addEventListener("focusin", focusGuard, true);
    document.addEventListener("keydown", keyboard, true);
    return () => {
      document.removeEventListener("focusin", focusGuard, true);
      document.removeEventListener("keydown", keyboard, true);
      for (const [element, inert] of changed) element.inert = inert;
      document.body.style.overflow = priorOverflow;
      if (!document.querySelector("[data-stage-modal]") && opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, [open, ref, close]);
}
